import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cc-cookie-consent-v1";

/**
 * ICO-compliant cookie consent banner.
 * Options: Accept all · Reject non-essential · Preferences.
 * Blocks PostHog analytics until the user accepts (via posthog.opt_in_capturing).
 */
export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, preferences: false });

  useEffect(() => {
    let stored;
    try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { stored = null; }
    if (!stored) {
      setVisible(true);
      try { window.posthog?.opt_out_capturing?.(); } catch {}
    } else {
      applyConsent(stored);
    }
  }, []);

  const applyConsent = (choice) => {
    try {
      if (choice.analytics) window.posthog?.opt_in_capturing?.();
      else window.posthog?.opt_out_capturing?.();
    } catch {}
  };

  const persist = (choice) => {
    const full = { ...choice, ts: new Date().toISOString() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(full)); } catch {}
    applyConsent(full);
    setVisible(false);
  };

  const acceptAll = () => persist({ essential: true, analytics: true, preferences: true });
  const rejectNonEssential = () => persist({ essential: true, analytics: false, preferences: false });
  const saveCustom = () => persist(prefs);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] pointer-events-none px-4 pb-4 lg:px-6 lg:pb-6"
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-heading"
    >
      <div className="pointer-events-auto max-w-[1200px] mx-auto border hairline-strong bg-white shadow-[0_6px_0_0_#111]">
        {!showPrefs ? (
          <div className="p-5 lg:p-6 flex flex-col lg:flex-row items-start gap-5">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Cookie className="w-5 h-5 text-[#ff4500] shrink-0 mt-0.5" strokeWidth={1.75} />
              <div className="min-w-0">
                <div id="cookie-heading" className="font-display text-lg mb-1">{t("cookie.heading")}</div>
                <p className="text-sm text-[#444] leading-relaxed">
                  {t("cookie.body")}{" "}
                  <Link to="/cookies" className="link-underline text-[#ff4500]">{t("cookie.readPolicy")}</Link>.
                </p>
                <p className="text-[10px] mono uppercase tracking-wider text-[#888] mt-2" data-testid="cookie-ico-line">
                  Registered with the UK ICO · CSN5693592
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button onClick={rejectNonEssential} className="btn-secondary !px-4 !py-2.5 text-xs" data-testid="cookie-reject">
                {t("cookie.rejectNonEssential")}
              </button>
              <button onClick={() => setShowPrefs(true)} className="btn-secondary !px-4 !py-2.5 text-xs" data-testid="cookie-customise">
                {t("cookie.customise")}
              </button>
              <button onClick={acceptAll} className="btn-primary !px-4 !py-2.5 text-xs" data-testid="cookie-accept">
                {t("cookie.acceptAll")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 lg:p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="font-display text-lg mb-1">{t("cookie.prefsHeading")}</div>
                <p className="text-xs text-[#555]">{t("cookie.prefsBody")}</p>
              </div>
              <button onClick={() => setShowPrefs(false)} aria-label={t("cookie.closePrefs")} data-testid="cookie-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <PrefRow label={t("cookie.essentialLabel")} desc={t("cookie.essentialDesc")} locked checked={prefs.essential} alwaysOnLabel={t("cookie.alwaysOn")} />
              <PrefRow label={t("cookie.analyticsLabel")} desc={t("cookie.analyticsDesc")} checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })} testid="pref-analytics" alwaysOnLabel={t("cookie.alwaysOn")} />
              <PrefRow label={t("cookie.preferencesLabel")} desc={t("cookie.preferencesDesc")} checked={prefs.preferences}
                onChange={(v) => setPrefs({ ...prefs, preferences: v })} testid="pref-preferences" alwaysOnLabel={t("cookie.alwaysOn")} />
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={rejectNonEssential} className="btn-secondary !px-4 !py-2.5 text-xs" data-testid="cookie-prefs-reject">
                {t("cookie.rejectAll")}
              </button>
              <button onClick={saveCustom} className="btn-primary !px-4 !py-2.5 text-xs" data-testid="cookie-prefs-save">
                {t("cookie.saveChoice")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const PrefRow = ({ label, desc, checked, onChange, locked, testid, alwaysOnLabel }) => (
  <label className={`flex items-start gap-3 p-3 border hairline ${locked ? "bg-[#fafafa]" : "bg-white cursor-pointer hover:border-[#ff4500]"}`}>
    <input
      type="checkbox"
      checked={checked}
      disabled={locked}
      onChange={(e) => !locked && onChange(e.target.checked)}
      className="mt-1"
      data-testid={testid}
    />
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-display text-sm">{label}</span>
        {locked && <span className="text-[10px] mono uppercase tracking-wider text-[#888]">{alwaysOnLabel}</span>}
      </div>
      <div className="text-xs text-[#555] mt-0.5">{desc}</div>
    </div>
  </label>
);
